import React from "react";
import { compose, withAppContext, withActions } from "containers/HOC";
import { Form, Field } from "react-final-form";
import { FORM_ERROR } from "final-form";
import Modal from "components/Modal/Modal";
import ModalBody from "components/Modal/ModalBody";
import ModalFooter from "components/Modal/ModalFooter";
import ModalFooterLeft from "components/Modal/ModalFooterLeft";
import ModalFooterRight from "components/Modal/ModalFooterRight";
import Button from "components/Button/Button";
import TagSelector from "./TagSelector";
import { getMyFarmTags, updateMyFarmTags } from "./_actions";

const FarmTagsModal = ({ farms, getMyFarmTags, updateMyFarmTags, close, online }) => {
    const [viewModel, setViewModel] = React.useState();
    const [availableTags, setAvailableTags] = React.useState([]);

    React.useEffect(() => {
        let isMounted = true;
        const fetchData = async (farms, getMyFarmTags) => {
            try {
                const farmTags = await getMyFarmTags();
                const farmsWithTags = farms.map((farm) => {
                    const tags = (farmTags.find((t) => t.farmId === farm.id) || {}).tags || [];
                    return { ...farm, tags };
                });
                const availableTags = farmsWithTags.reduce((tags, farmWithTags) => {
                    const newTags = farmWithTags.tags.filter((tag) => !tags.includes(tag));
                    return tags.concat(newTags);
                }, []);
                if (isMounted) {
                    setViewModel({ farmsWithTags });
                    setAvailableTags(availableTags);
                }
            } catch (fetchError) {
                if (isMounted) setViewModel({ fetchError });
            }
        };
        fetchData(farms, getMyFarmTags);
        return () => (isMounted = false);
    }, [farms, getMyFarmTags]);

    const handleTagAdded = (newTag) => {
        if (newTag) setAvailableTags([...availableTags, newTag]);
    };

    const submit = (closeModal) => async (viewModel) => {
        const farmTags = viewModel.farmsWithTags.map((farm) => ({ farmId: farm.id, tags: farm.tags }));
        try {
            await updateMyFarmTags(farmTags);
            closeModal();
        } catch (error) {
            return { [FORM_ERROR]: error };
        }
    };

    const info = "Farm tags are used to filter farms by tag. Select an existing tag from the list or begin typing in the list to add a new tag.";

    return (
        <Form initialValues={viewModel} onSubmit={submit(close)}>
            {({ values, handleSubmit, submitting, submitError, dirtySinceLastSubmit }) => {
                const { farmsWithTags, fetchError } = values;
                const error = fetchError || (!dirtySinceLastSubmit && submitError);
                return (
                    <form onSubmit={handleSubmit}>
                        <Modal title="Farm tags" close={close} submitting={submitting} wide>
                            <ModalBody loading={!farmsWithTags} info={info} error={error}>
                                {farmsWithTags && farmsWithTags.length > 0 && (
                                    <>
                                        <div className="Table u-mt-md">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Farm</th>
                                                        <th>Address</th>
                                                        <th data-width="50">Tags</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {farmsWithTags.map((farm, farmIndex) => {
                                                        return (
                                                            <tr key={farm.id} valign="top">
                                                                <td className="u-pt-lg">{farm.name}</td>
                                                                <td className="u-pt-lg">{farm.address}</td>
                                                                <td>
                                                                    <Field name={`farmsWithTags[${farmIndex}].tags`} availableTags={availableTags} onTagAdded={handleTagAdded} component={TagSelector} />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </ModalBody>
                            <ModalFooter>
                                <ModalFooterLeft>
                                    <Button id="cancel" onClick={close} secondary disabled={submitting}>
                                        Cancel
                                    </Button>
                                </ModalFooterLeft>
                                <ModalFooterRight>
                                    <Button id="submit" submit primary disabled={!online || fetchError || submitting}>
                                        Save
                                    </Button>
                                </ModalFooterRight>
                            </ModalFooter>
                        </Modal>
                    </form>
                );
            }}
        </Form>
    );
};

export default compose(withAppContext, withActions({ getMyFarmTags, updateMyFarmTags }))(FarmTagsModal);
